import { PortalCardTypes, ValidationErrors } from '@api/v4/private/PortalCardTypes';

export const ValidationSettings = {
  'one-third': {
    aspectRatio: (300 / 220),
    nameLength: 30,
    descriptionLength: 60,
  },
  'two-thirds': {
    aspectRatio: (630 / 220),
    nameLength: 60,
    descriptionLength: 150,
  },
  full: {
    aspectRatio: (960 / 220),
    nameLength: 100,
    descriptionLength: 250,
  }
};

const validLink = (link: string): boolean => (
  /^https?/i.test(link)
);

const validateName = ({ name, displayWidth }, cardType: PortalCardTypes): ValidationErrors[] => {
  if (!name) {
    return [ValidationErrors.MissingName];
  }
  if (name && displayWidth && name.length > ValidationSettings[displayWidth].nameLength) {
    return [ValidationErrors.NameLengthExceeded];
  }
  if (name && [PortalCardTypes.PortalLink].includes(cardType) && name.length > 100) {
    return [ValidationErrors.NameLengthExceeded];
  }
  if (name && [PortalCardTypes.PortalWidget].includes(cardType) && name.length > 300) {
    return [ValidationErrors.NameLengthExceeded];
  }
  return [];
};

const validateDescription = ({ description, displayWidth }): ValidationErrors[] => {
  if (description && displayWidth && description.length > ValidationSettings[displayWidth].descriptionLength) {
    return [ValidationErrors.DescriptionLengthExceeded];
  }

  return [];
};

const validateLink = ({ linkUrl }, cardType: PortalCardTypes): ValidationErrors[] => {
  const requiresValidation = [PortalCardTypes.PortalLink, PortalCardTypes.PortalTile].includes(cardType);
  if (requiresValidation && !linkUrl) {
    return [ValidationErrors.MissingLink];
  }

  if (requiresValidation && !validLink(linkUrl)) {
    return [ValidationErrors.InvalidLink];
  }

  return [];
};

const validateWidgetResource = ({ widgetResource }, cardType: PortalCardTypes): ValidationErrors[] => {
  const requiresValidation = [PortalCardTypes.PortalWidget].includes(cardType);
  if (requiresValidation && (!widgetResource || !widgetResource.key || !widgetResource.type)) {
    return [ValidationErrors.MissingResource];
  }

  return [];
};

const validateSubType = ({ subType }, cardType: PortalCardTypes): ValidationErrors[] => {
  const requiresValidation = [PortalCardTypes.PortalWidget].includes(cardType);
  if (requiresValidation && !subType) {
    return [ValidationErrors.MissingSubType];
  }

  return [];
};

export const validate = (cardType: PortalCardTypes, editState: any): ValidationErrors[] => (
  [
    ...validateName(editState, cardType),
    ...validateDescription(editState),
    ...validateLink(editState, cardType),
    ...validateWidgetResource(editState, cardType),
    ...validateSubType(editState, cardType)
  ]
);

export const handleValidations = (
  cardType: PortalCardTypes,
  setValidationErrors: (errors: ValidationErrors[]) => void,
  editState: any
): boolean => {
  const submissionErrors = validate(cardType, editState);
  if (submissionErrors.length > 0) {
    setValidationErrors(submissionErrors);
    return true;
  }
  return false;
};

export const nameError = (validationErrors: ValidationErrors[]): string => {
  if (validationErrors.includes(ValidationErrors.MissingName)) {
    return ValidationErrors.MissingName;
  }
  if (validationErrors.includes(ValidationErrors.NameLengthExceeded)) {
    return ValidationErrors.NameLengthExceeded;
  }
  return null;
};

export const linkError = (validationErrors: ValidationErrors[]): string => {
  if (validationErrors.includes(ValidationErrors.MissingLink)) {
    return ValidationErrors.MissingLink;
  }
  if (validationErrors.includes(ValidationErrors.InvalidLink)) {
    return ValidationErrors.InvalidLink;
  }
  return null;
};

export const descriptionError = (validationErrors: ValidationErrors[]): string => {
  if (validationErrors.includes(ValidationErrors.DescriptionLengthExceeded)) {
    return ValidationErrors.DescriptionLengthExceeded;
  }
  return null;
};

export const widgetResourceError = (validationErrors: ValidationErrors[]): string => {
  if (validationErrors.includes(ValidationErrors.MissingResource)) {
    return ValidationErrors.MissingResource;
  }
  return null;
};

export const subTypeError = (validationErrors: ValidationErrors[]): string => {
  if (validationErrors.includes(ValidationErrors.MissingSubType)) {
    return ValidationErrors.MissingSubType;
  }
  return null;
};
